/** Polish-locale number formatting used identically on client and server (notifications, CSV labels). */

export function fmt(v: number | null | undefined, d: number): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("pl-PL", { minimumFractionDigits: d, maximumFractionDigits: d });
}

/** Sensible decimals for a USD price: 4120 → 0, 16.9 → 2, 0.92 → 3, 0.05 → 4 */
export function priceDecimals(v: number): number {
  const a = Math.abs(v);
  return a >= 1000 ? 0 : a >= 100 ? 1 : a >= 1 ? 2 : a >= 0.1 ? 3 : a >= 0.01 ? 4 : 6;
}

export function price(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `$${fmt(v, priceDecimals(v))}`;
}

export function usd(v: number | null | undefined, d = 0): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `$${fmt(v, d)}`;
}

/** "+$150" / "−$40" — for profit figures; usd() alone would print "$-40". */
export function signedUsd(v: number | null | undefined, d = 0): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}$${fmt(Math.abs(v), d)}`;
}

export function pln(v: number | null | undefined, rate: number | null, d = 0): string {
  if (v == null || rate == null || !Number.isFinite(v)) return "—";
  return `${fmt(v * rate, d)} PLN`;
}

export function pct(v: number | null | undefined, d = 1, sign = true): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${sign && v > 0 ? "+" : ""}${fmt(v, d)} %`;
}

/** Percentage points, e.g. "+1,4 pp" */
export function pp(v: number | null | undefined, d = 1): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v > 0 ? "+" : ""}${fmt(v, d)} pp`;
}

/** 497e9 → "497,00 B", 0.94e12 → "940,00 B", 2.1e12 → "2,10 T", 760e6 → "760 M" */
export function big(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  if (v >= 1e12) return `${fmt(v / 1e12, 2)} T`;
  if (v >= 1e9) return `${fmt(v / 1e9, 2)} B`;
  return `${fmt(v / 1e6, 0)} M`;
}

/** Token / unit counts (not money): 1.2e9 → "1,20 B", 172.9e6 → "172,9 M", 36 666 → "36 666". */
export function count(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  if (v >= 1e12) return `${fmt(v / 1e12, 2)} T`;
  if (v >= 1e9) return `${fmt(v / 1e9, 2)} B`;
  if (v >= 1e6) return `${fmt(v / 1e6, 1)} M`;
  return fmt(v, 0);
}

export function multiplier(m: number): string {
  return `×${fmt(m, Number.isInteger(m) ? 0 : 1)}`;
}

export function dateTimePl(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return `${d.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" })} ${d.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}`;
}

export function datePl(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return d.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** "12 wrz" — short day+month for chart axis labels; `day` is a `YYYY-MM-DD` calendar date. */
export function shortDate(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** "4 min temu", "przed chwilą", "2 godz. temu" */
export function ago(iso: string | Date | null | undefined, now = new Date()): string {
  if (!iso) return "nigdy";
  const t = typeof iso === "string" ? new Date(iso).getTime() : iso.getTime();
  const s = Math.max(0, Math.round((now.getTime() - t) / 1000));
  if (s < 45) return "przed chwilą";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min temu`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} godz. temu`;
  return `${Math.round(h / 24)} dni temu`;
}
